import { useEffect, useRef, useState } from 'react'
import { apiService, type MatchHighlight } from '@/service/api'
import type { PreviewMatch } from './types'

const DEBOUNCE_MS = 250

interface ImageSearchState {
  /** One entry per match, in the same order as `boxes`. */
  matches: PreviewMatch[]
  /** Bounding boxes per match, in the converted PDF's coordinate space. */
  boxes: MatchHighlight[]
  /** Coordinate space the boxes are in — scale to the rendered image with this. */
  pageWidth: number
  pageHeight: number
  isSearching: boolean
}

const EMPTY: ImageSearchState = {
  matches: [],
  boxes: [],
  pageWidth: 0,
  pageHeight: 0,
  isSearching: false,
}

/**
 * FS-657: find-in-image for a card preview backed by a PNG/JPG.
 *
 * An uploaded image is converted to a single-page PDF at indexing time
 * (`convert_image_to_pdf`), and the page is created at exactly the image's size
 * with the image inserted into the full page rect — no letterboxing. So the
 * indexed word positions map onto the displayed image by a single ratio,
 * `naturalWidth / pageWidth`, with no offset. (The document's stored location is
 * never updated to that converted PDF, which is why these previews still render
 * as images rather than going through PdfJsViewer.)
 *
 * Deliberately not shared with PdfJsViewer's search: that one also owns
 * cross-page navigation, per-page counts and a text-layer fallback, none of
 * which exist for a single-page image.
 */
export function useImageMatches(docId: string | undefined, query: string): ImageSearchState {
  const [result, setResult] = useState<ImageSearchState>(EMPTY)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const term = query.trim()
    if (!docId || !term) {
      abortRef.current?.abort()
      setResult(EMPTY)
      return
    }

    let cancelled = false
    setResult((prev) => ({ ...prev, isSearching: true }))

    const timer = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      try {
        const response = await apiService.searchDocuments(term, [docId])
        if (cancelled || controller.signal.aborted) return

        // The converted PDF has a single page, so take the first page that came
        // back rather than assuming an index.
        const page = response.results.find((r) => r.page_highlights?.matches.length)
        const highlights = page?.page_highlights
        if (!highlights) {
          // No word positions for this document (never indexed, or indexed before
          // positions were extracted), so there is nothing to draw over the image.
          setResult({ ...EMPTY, isSearching: false })
          return
        }
        setResult({
          matches: highlights.matches.map((match, i) => ({
            id: `${page?.page ?? 0}-${i}`,
            locationLabel: '',
            snippet: match.snippet ?? page?.text ?? term,
          })),
          boxes: highlights.matches,
          pageWidth: highlights.page_width,
          pageHeight: highlights.page_height,
          isSearching: false,
        })
      } catch {
        if (!cancelled && !controller.signal.aborted) setResult({ ...EMPTY, isSearching: false })
      }
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [docId, query])

  return result
}
